import {
  CPO_ELECTRA,
  LayerId,
  MIN_HPC_SPOTS,
  STATION_CHARGE_FILTERS,
  TRAFFIC_FILTERS,
} from '../constants';
import { chargeFallsInStationRange } from './getStationByCharges';
import type { LayerVisibilityState } from '../hooks/useLayerVisibilityFilters';
import {
  COMPETITOR_HEATMAP_LAYER_IDS,
  COMPETITOR_TIER_LAYER_IDS,
  type MapLayersService,
} from './MapLayersService';

const MAX_AADT_LAYERS = [LayerId.TRAFFIC_MAX_AADT, LayerId.MAX_AADT_CIRCLE];

/** Preserve TRAFFIC_FILTERS order — the array is the contract, not key order. */
function visibleTrafficRanges(traffic: LayerVisibilityState['traffic']) {
  return TRAFFIC_FILTERS.filter((id) => traffic[id]);
}

/**
 * Same toggles applied to the map-level tiles layer. Separate entry point
 * because `applyMaxAadtVisibility` runs only once a location's detail layers
 * exist, whereas the tiles render with nothing selected.
 */
export function applyTrafficTilesVisibility(
  service: MapLayersService,
  traffic: LayerVisibilityState['traffic'],
): void {
  const visibleTrafficIds = visibleTrafficRanges(traffic);

  service.toggleLayerVisibility([LayerId.TRAFFIC_TILES], visibleTrafficIds.length > 0);
  if (visibleTrafficIds.length) service.filterTraffic(LayerId.TRAFFIC_TILES, visibleTrafficIds);
}

/** Preserve STATION_CHARGE_FILTERS order — the array is the contract. */
function visibleStationLabels(station: LayerVisibilityState['station']) {
  return STATION_CHARGE_FILTERS.filter((label) => station[label]);
}

/**
 * Whether a stored winning station would still be drawn by the competitor tiles.
 * Electra is its own toggle. Everyone else needs a visible charge range and,
 * when low-HPC stations are hidden, at least `MIN_HPC_SPOTS`.
 */
export function competitorMatchesStationFilters(
  competitor: { performance: number; network: string; hpc_spots: number },
  state: StationVisibilityState,
): boolean {
  // The scorer drops Electra, so a stored winner never takes this branch. It
  // mirrors the tile filter for a row that still carries that network name.
  if (competitor.network === CPO_ELECTRA) return state.isElectraVisible;

  const minHpcSpots = state.isLowHpcVisible ? 0 : MIN_HPC_SPOTS;
  if (minHpcSpots > 0 && competitor.hpc_spots < minHpcSpots) return false;

  return visibleStationLabels(state.station).some((label) =>
    chargeFallsInStationRange(competitor.performance, label),
  );
}

/** The subset of the panel state the station layers read. */
export type StationVisibilityState = Pick<
  LayerVisibilityState,
  'station' | 'isElectraVisible' | 'isLowHpcVisible'
>;

/**
 * Same station toggles applied to the map-level tiles layer, for the same reason
 * `applyTrafficTilesVisibility` exists. The session clusters are a separate flag:
 * hiding them leaves the stations, and hiding the stations leaves them.
 * The winning-station crown is not in this toggle. `useHighestPerformingCompetitorMark`
 * draws it only while the open station still matches these filters.
 */
export function applyCompetitorsTilesVisibility(
  service: MapLayersService,
  state: StationVisibilityState,
  isClustersVisible: boolean,
): void {
  const visibleLabels = visibleStationLabels(state.station);
  const stationsVisible = visibleLabels.length > 0 || state.isElectraVisible;

  service.toggleLayerVisibility(COMPETITOR_TIER_LAYER_IDS, stationsVisible);
  service.toggleLayerVisibility([...COMPETITOR_HEATMAP_LAYER_IDS], isClustersVisible);
  if (!stationsVisible) return;

  // `filterStations` ANDs each tier's predicate in, so both take the same filter.
  for (const layerId of COMPETITOR_TIER_LAYER_IDS) {
    service.filterStations(
      layerId,
      visibleLabels,
      state.isElectraVisible,
      state.isLowHpcVisible ? 0 : MIN_HPC_SPOTS,
    );
  }
}

/**
 * The AADT-range toggles applied to the selected location's max-AADT highlight
 * and its circle — the only per-location layers a range can hide, since the road
 * network itself is served as tiles. Idempotent; performs NO React state changes.
 */
export function applyMaxAadtVisibility(
  service: MapLayersService,
  traffic: LayerVisibilityState['traffic'],
): void {
  const visibleTrafficIds = visibleTrafficRanges(traffic);

  if (visibleTrafficIds.length === 0) {
    service.toggleLayerVisibility(MAX_AADT_LAYERS, false);
    return;
  }

  service.toggleLayerVisibility(MAX_AADT_LAYERS, true);
  service.filterTraffic(LayerId.TRAFFIC_MAX_AADT, visibleTrafficIds);
}
